import { useState, useEffect } from "react";
import { Flex, Text } from "@radix-ui/themes";
import { MenuItem } from "components";
import { Outlet, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useProfileContext } from "context";
import { WrenchScrewdriverIcon, ClockIcon, UsersIcon, SpeakerWaveIcon, ClipboardDocumentListIcon,
  PresentationChartLineIcon, AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";

export function MarketingLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useProfileContext();

  // Helper function to get menu state from path
  const getMenuStateFromPath = (path) => {
    if (path.includes("/marketing/analytics")) return "analytics";
    if (path.includes("/marketing/automations")) return "automations";
    if (path.includes("/marketing/templates")) return "templates";
    if (path.includes("/marketing/campaigns")) return "campaigns";
    if (path.includes("/marketing/audience")) return "audience";
    if (path.includes("/marketing/activity")) return "activity";
    if (path.includes("/marketing/settings")) return "settings";
    return "analytics"; // default path
  };

  // State to manage menu selection
  const [menuSelection, setMenuSelection] = useState(getMenuStateFromPath(location.pathname));

  // Function to handle menu selection
  const handleMenuSelection = (flag) => {
    setMenuSelection(flag);
    navigate(flag);
  };

  // Update menu selection on path change
  useEffect(() => {
    setMenuSelection(getMenuStateFromPath(location.pathname));
  }, [location.pathname]);

  return (
    <Flex direction="column" gap="5" height="100%" 
      style={{ alignSelf: "stretch", alignItems: "flex-start", flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>
      {/* Main content */}
      <Flex className="main-content-container" gap="8" px="5" py="5"
        style={{ alignSelf: "stretch", flex: "1 1 0", alignItems: "flex-start", minHeight: 0, overflow: "hidden" }}>
        {/* Sidebar menu */}
        <Flex className="main-content-sidebar-menu" width="144px" direction="column" gap="1" height="100%" 
          style={{ alignItems: "flex-start", minHeight: 0 }}>
          {/* Analytics submenu */}
          <MenuItem
            flag="analytics"
            state={menuSelection}
            onClick={handleMenuSelection}
            text="Analytics"
            tooltip="View analytics and insights on your marketing campaigns"
            icon={<PresentationChartLineIcon height={16} width={16} />}
          />
          {/* Automations submenu */}
          {userProfile?.user_role !== 'Manager' && (
            <MenuItem
              flag="automations" 
              state={menuSelection}
              onClick={handleMenuSelection}
              text="Automations"
              tooltip="Automate your marketing workflows and campaigns"
              icon={<AdjustmentsHorizontalIcon height={16} width={16} />}
            />
          )}
          {/* Templates submenu */}
          <MenuItem
            flag="templates"
            state={menuSelection}
            onClick={handleMenuSelection}
            text="Templates"
            tooltip="Manage and create marketing templates"
            icon={<ClipboardDocumentListIcon height={16} width={16} />}
          />
          {/* Campaigns submenu */}
          <MenuItem
            flag="campaigns"
            state={menuSelection}
            onClick={handleMenuSelection}
            text="Campaigns"
            tooltip="View and manage your marketing campaigns"
            icon={<SpeakerWaveIcon height={16} width={16} />}
          />
          {/* Audience submenu */}
          <MenuItem
            flag="audience"
            state={menuSelection} onClick={handleMenuSelection} text="Audience"
            tooltip="Analyze and segment your audience"
            icon={<UsersIcon height={16} width={16} />} 
          />
          {/* Log activity submenu */}
          {userProfile?.user_role !== 'Manager' && (
            <MenuItem
              flag="activity"
              state={menuSelection}
              onClick={handleMenuSelection}
              text="Activity"
              tooltip="View recent marketing activities and their respective logs"
              icon={<ClockIcon height={16} width={16} />}
            />
          )}
          {/* Settings submenu */}
          <MenuItem
            flag="settings"
            state={menuSelection}
            onClick={handleMenuSelection} 
            text="Settings"
            tooltip="Manage your marketing configurational settings"
            icon={<WrenchScrewdriverIcon height={16} width={16} />}
          />
        </Flex>
        {/* Main content area */}
        <Flex style={{ alignSelf: "stretch", flex: "1 0 0", alignItems: "flex-start", height: "100%", minHeight: 0, minWidth: 0 }}>
          <Outlet />
        </Flex>
      </Flex>
    </Flex>
  );
}
